import React from "react";
import { motion } from "framer-motion";
import { FaBriefcase, FaAward, FaBuilding, FaCheckCircle, FaCalendarAlt, FaMapMarkerAlt } from "react-icons/fa";
import { HiSparkles } from "react-icons/hi";
import { BottomLine } from "../../../components";

const Timeline = () => {

  const cards = [
    {
      id: 1,
      badge: "Full-Time",
      badgeColor: "bg-violet-500/20 text-violet-300 border border-violet-500/30",
      dotColor: "bg-violet-400",
      iconBg: "from-violet-600 to-violet-700",
      accentBar: "from-violet-500 to-indigo-500",
      glowColor: "group-hover:shadow-violet-500/25",
      icon: <FaBuilding className="text-lg" />,
      title: "Senior Full Stack Developer",
      company: "Nexora",
      period: "Present",
      location: "Remote",
      isActive: true,
      description:
        "Building and scaling production-grade web applications at Nexora using modern full-stack technologies, clean architecture, and a strong focus on performance and user experience.",
      achievements: [
        "Team member at Nexora as Senior Full Stack Dev",
        "Full-stack development with React, Node.js & cloud",
        "Collaborating to deliver production-ready solutions",
        "Maintaining code quality and best practices",
      ],
    },
    {
      id: 2,
      badge: "Freelance",
      badgeColor: "bg-indigo-500/20 text-indigo-300 border border-indigo-500/30",
      dotColor: "bg-indigo-400",
      iconBg: "from-indigo-600 to-violet-600",
      accentBar: "from-indigo-500 to-violet-500",
      glowColor: "group-hover:shadow-indigo-500/25",
      icon: <FaBriefcase className="text-lg" />,
      title: "Full-Stack Developer",
      company: "Freelance | Remote",
      period: "2023 – Present",
      location: "Work From Home",
      isActive: true,
      description:
        "Delivering end-to-end web solutions with React.js/Next.js frontends, Node.js/Express backends, and optimized MongoDB/PostgreSQL database architectures for clients worldwide.",
      achievements: [
        "Successfully delivered 50+ full-stack applications",
        "3+ years of hands-on production experience",
        "98% client retention through exceptional quality",
        "Expert in frontend architecture & backend systems",
      ],
    },
    {
      id: 3,
      badge: "Learning",
      badgeColor: "bg-violet-500/20 text-violet-300 border border-violet-500/30",
      dotColor: "bg-violet-400",
      iconBg: "from-violet-600 to-indigo-600",
      accentBar: "from-violet-500 to-indigo-500",
      glowColor: "group-hover:shadow-violet-500/25",
      icon: <FaAward className="text-lg" />,
      title: "Certifications & Tech Growth",
      company: "Online Programs & Self-Learning",
      period: "2023 – Present",
      location: "Remote",
      isActive: true,
      description:
        "Continuously upgrading skills through industry-relevant certifications and hands-on practice — focusing on modern JavaScript ecosystem, cloud deployment, and full-stack best practices.",
      achievements: [
        "Full-Stack Web Dev & React/Node.js certifications",
        "Database design & cloud deployment fundamentals",
        "Staying current with latest tools & frameworks",
        "Applying best practices in every project",
      ],
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  };

  return (
    <section className="relative py-16 md:py-20 lg:py-24 px-4 sm:px-6 md:px-10 lg:px-14 bg-[#0c0a16] text-white overflow-hidden">

      {/* Background glow blobs */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-violet-700/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-indigo-700/10 rounded-full blur-3xl pointer-events-none" />

      {/* Section Heading */}
      <motion.div
        className="mb-12 md:mb-16 text-center relative z-10"
        initial={{ y: -40, opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, type: "spring" }}
      >
        <h1 className="text-3xl md:text-4xl font-semibold mb-2 md:mb-3">
          My <span className="text-violet-400">Journey</span>
        </h1>
        <h3 className="text-gray-400 text-sm md:text-base tracking-wide">
          From hands-on experience to continuous learning and growth
        </h3>
        <BottomLine />
      </motion.div>

      {/* Cards Grid */}
      <motion.div
        className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-7 w-full max-w-6xl mx-auto relative z-10"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.15 }}
      >
        {cards.map((card) => (
          <motion.div
            key={card.id}
            variants={itemVariants}
            whileHover={{ y: -6, transition: { duration: 0.25 } }}
            className={`group relative flex flex-col bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl
                        shadow-xl overflow-hidden transition-shadow duration-300 ${card.glowColor} hover:shadow-2xl`}
          >
            {/* Top gradient accent bar */}
            <div className={`h-1 w-full bg-gradient-to-r ${card.accentBar}`} />

            {/* Card body */}
            <div className="flex flex-col flex-grow p-5 sm:p-6">

              {/* Icon + Badge row */}
              <div className="flex items-center justify-between mb-4">
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${card.iconBg}
                                flex items-center justify-center text-white shadow-lg`}>
                  {card.icon}
                </div>
                <div className="flex items-center gap-2">
                  {card.isActive && (
                    <span className="flex items-center gap-1.5 text-xs font-medium">
                      <span className={`w-2 h-2 rounded-full ${card.dotColor} animate-pulse`} />
                      <span className="text-gray-300">Active</span>
                    </span>
                  )}
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${card.badgeColor}`}>
                    {card.badge}
                  </span>
                </div>
              </div>

              {/* Title */}
              <h3 className="text-lg md:text-xl font-bold text-white mb-1 leading-snug">
                {card.title}
              </h3>

              {/* Company & Period */}
              <div className="flex flex-col gap-1 mb-3">
                <div className="flex items-center gap-1.5 text-xs text-gray-400">
                  <FaBuilding className="text-violet-400 flex-shrink-0" />
                  <span className="font-medium text-gray-300">{card.company}</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-400">
                  <FaCalendarAlt className="text-violet-400 flex-shrink-0" />
                  <span>{card.period}</span>
                  <span className="mx-1 text-gray-600">·</span>
                  <FaMapMarkerAlt className="text-violet-400 flex-shrink-0" />
                  <span>{card.location}</span>
                </div>
              </div>

              {/* Divider */}
              <div className="h-px bg-white/8 mb-3" />

              {/* Description */}
              <p className="text-sm text-gray-400 leading-relaxed mb-4 flex-grow">
                {card.description}
              </p>

              {/* Achievements */}
              <div className="space-y-2">
                {card.achievements.map((ach, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <FaCheckCircle className="text-violet-400 mt-0.5 flex-shrink-0 text-xs" />
                    <span className="text-xs text-gray-300 leading-relaxed">{ach}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom gradient shine on hover */}
            <div className={`absolute inset-0 bg-gradient-to-t ${card.accentBar.replace('from-', 'from-').replace('to-', 'to-')}/0
                            opacity-0 group-hover:opacity-5 transition-opacity duration-500 pointer-events-none`} />
          </motion.div>
        ))}
      </motion.div>

      {/* Bottom CTA */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.4, duration: 0.6 }}
        className="max-w-3xl mx-auto mt-12 md:mt-14 lg:mt-16 text-center relative z-10"
      >
        <div className="relative bg-gradient-to-r from-violet-900/25 via-violet-900/20 to-indigo-900/25
                        backdrop-blur-sm border border-violet-500/20 rounded-2xl p-6 md:p-8 overflow-hidden">
          {/* Inner glow */}
          <div className="absolute inset-0 bg-gradient-to-r from-violet-600/5 via-transparent to-indigo-600/5 pointer-events-none" />
          <div className="relative z-10">
            <div className="flex items-center justify-center gap-2 mb-3">
              <HiSparkles className="text-violet-400 text-xl" />
              <h3 className="text-lg md:text-xl font-bold text-white">
                Continuous Learning & Growth
              </h3>
              <HiSparkles className="text-indigo-400 text-xl" />
            </div>
            <p className="text-gray-400 text-sm md:text-base max-w-2xl mx-auto leading-relaxed mb-5">
              Every project is a new opportunity to learn and improve. I'm committed to staying
              updated with the latest technologies and delivering exceptional results.
            </p>
            <div className="h-0.5 w-20 md:w-28 bg-gradient-to-r from-violet-500 to-indigo-500 mx-auto rounded-full" />
          </div>
        </div>
      </motion.div>

    </section>
  );
};

export default Timeline;
